import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, LogIn, LogOut, Calendar as CalendarIcon, Clock, Map as MapIcon, ChevronRight, Info, CheckCircle2, Hourglass, Loader2, LocateFixed, RadioTower } from "lucide-react";
import { api, apiError, formatDate, getGeo, haversineKm, mapsUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PREVIEW_IMG = "https://images.unsplash.com/photo-1512573314-17727dcdf45c?crop=entropy&cs=srgb&fm=jpg&q=85";

const memberStatus = (m, checkoutAvailable) => {
  if (m.checkin && m.checkout) return { label: "Finalizado", cls: "text-indigo-300", Icon: CheckCircle2 };
  if (m.checkin && !m.checkout && checkoutAvailable) return { label: "Checkout pendente", cls: "text-amber-400", Icon: Hourglass };
  if (m.checkin) return { label: "Check-in feito", cls: "text-emerald-400", Icon: CheckCircle2 };
  return { label: "Aguardando", cls: "text-muted-foreground", Icon: Clock };
};

export default function CheckIn() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [team, setTeam] = useState(null);
  const [myGeo, setMyGeo] = useState(null);
  const [busy, setBusy] = useState("");
  const [gate, setGate] = useState(null);

  const load = useCallback(() => api.get(`/events/${id}`).then((r) => setEv(r.data)).catch((e) => setGate(apiError(e.response?.data?.detail) || "Evento não encontrado.")), [id]);
  const loadTeam = useCallback(() => api.get(`/events/${id}/team`).then((r) => setTeam(r.data)).catch((e) => { if (e.response?.status === 403) setGate("Apenas a equipe confirmada pode ver o check-in ao vivo deste evento."); }), [id]);

  useEffect(() => { load(); loadTeam(); getGeo().then(setMyGeo).catch(() => {}); }, [load, loadTeam]);
  useEffect(() => { if (gate) return; const t = setInterval(loadTeam, 8000); return () => clearInterval(t); }, [loadTeam, gate]);

  const refreshLocation = async () => {
    try { const g = await getGeo(); setMyGeo(g); toast.success("Localização atualizada."); }
    catch { toast.error("Permita o acesso à localização."); }
  };
  const doGeo = async (kind) => {
    if (!ev?.my_application?.id) return;
    setBusy(kind);
    try {
      const g = await getGeo(); setMyGeo(g);
      await api.post(`/applications/${ev.my_application.id}/${kind}`, g);
      toast.success(kind === "checkin" ? "Check-in realizado!" : "Check-out realizado!");
      await load(); await loadTeam();
    } catch (e) { toast.error(e.response ? apiError(e.response.data?.detail) : "Permita o acesso à localização."); }
    finally { setBusy(""); }
  };

  if (gate) return (
    <div className="max-w-lg mx-auto py-16 text-center" data-testid="checkin-gate">
      <RadioTower className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
      <p className="text-muted-foreground mb-5">{gate}</p>
      <Button onClick={() => navigate(`/eventos/${id}`)} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-xl">Voltar ao evento</Button>
    </div>
  );
  if (!ev) return <div className="max-w-3xl mx-auto py-20 text-center text-muted-foreground" data-testid="checkin-loading">Carregando…</div>;

  const myApp = ev.my_application;
  const isTeam = ev.is_creator || ["confirmado", "concluido"].includes(ev.my_status);
  const dist = (myGeo && ev.coords) ? Math.round(haversineKm(myGeo, ev.coords) * 1000) : null;
  const radius = ev.checkin_radius_m || 0;
  const within = dist != null && dist <= radius;
  const distLabel = dist != null ? (dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${dist} m`) : "—";
  const fill = dist == null ? 0 : within ? 100 : Math.max(8, Math.min(96, Math.round((radius / dist) * 100)));

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-6" data-testid="checkin-page">
      <div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Check-in do evento</h1>
        <p className="text-muted-foreground mt-1">Faça seu check-in no local do evento e acompanhe sua equipe em tempo real.</p>
      </div>

      {/* Card do evento */}
      <button data-testid="checkin-event-card" onClick={() => navigate(`/eventos/${id}`)}
        className="w-full text-left rounded-2xl overflow-hidden border border-accent/40 shadow-[0_0_24px_rgba(255,170,0,0.12)] flex">
        <div className="w-28 sm:w-36 shrink-0 bg-cover bg-center relative" style={{ backgroundImage: `linear-gradient(to right, rgba(15,15,18,0.2), rgba(15,15,18,0.85)), url(${PREVIEW_IMG})` }}>
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FF4D00] text-white">Evento</span>
        </div>
        <div className="flex-1 min-w-0 p-4 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="font-head text-lg font-bold truncate">{ev.name}</h2>
            <div className="mt-1.5 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground"><CalendarIcon className="h-4 w-4 text-accent shrink-0" />{formatDate(ev.date)}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4 text-accent shrink-0" />{ev.start_time} – {ev.end_time}</div>
              <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4 text-accent shrink-0" /><span className="truncate">{ev.address || "Local a definir"}</span></div>
              {ev.coords && <span onClick={(e) => { e.stopPropagation(); window.open(mapsUrl(ev), "_blank"); }} className="inline-flex items-center gap-1 text-[#FF4D00] text-sm font-medium"><MapIcon className="h-4 w-4" />Ver no mapa</span>}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
        </div>
      </button>

      {/* Sua localização */}
      {isTeam && (
        <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="location-card">
          <div className="flex items-center gap-2 mb-3"><MapPin className="h-5 w-5 text-accent" /><span className="font-head font-bold">Sua localização</span></div>
          <div className="grid grid-cols-2 gap-4 items-center">
            <div className="flex items-center gap-3">
              <div className={`h-14 w-14 rounded-full flex items-center justify-center shrink-0 ${within ? "bg-emerald-500/15" : "bg-amber-500/15"}`}><LocateFixed className={`h-6 w-6 ${within ? "text-emerald-400" : "text-amber-400"}`} /></div>
              <div>
                <div className={`font-head text-3xl font-extrabold leading-none ${within ? "text-emerald-400" : "text-amber-400"}`} data-testid="distance-value">{distLabel}</div>
                <div className="text-xs text-muted-foreground mt-1">de distância do evento</div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Raio permitido</div>
              <div className="font-head text-2xl font-bold" data-testid="radius-value">{radius} m</div>
              <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden"><div className={`h-full rounded-full ${within ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${fill}%` }} /></div>
              <div className={`text-xs mt-1.5 font-medium ${within ? "text-emerald-400" : dist == null ? "text-muted-foreground" : "text-amber-400"}`} data-testid="radius-hint">
                {dist == null ? "Toque em atualizar localização" : within ? "Você está dentro do raio" : "Fora do raio permitido"}
              </div>
            </div>
          </div>
          {!ev.coords && <p className="text-xs text-muted-foreground mt-3">O organizador não definiu as coordenadas; a distância fica indisponível, mas o check-in continua sendo validado no servidor.</p>}
          <button data-testid="refresh-location-btn" onClick={refreshLocation} className="mt-3 text-sm text-accent flex items-center gap-1 hover:underline"><LocateFixed className="h-4 w-4" />Atualizar localização</button>
        </div>
      )}

      {/* Ações check-in/out */}
      {isTeam && myApp && (
        <div className="grid grid-cols-2 gap-3" data-testid="checkin-actions">
          <Button data-testid="checkin-btn" onClick={() => doGeo("checkin")} disabled={busy === "checkin" || !!myApp.checkin || !ev.checkin_available}
            className="h-14 rounded-2xl font-bold gap-2 bg-gradient-to-r from-[#FF4D00] to-accent text-white shadow-[0_0_20px_rgba(255,79,0,0.35)] disabled:opacity-40 disabled:shadow-none">
            {busy === "checkin" ? <Loader2 className="h-5 w-5 animate-spin" /> : <MapPin className="h-5 w-5" />}{myApp.checkin ? "Check-in feito" : "Fazer check-in"}
          </Button>
          <Button data-testid="checkout-btn" onClick={() => doGeo("checkout")} disabled={busy === "checkout" || !myApp.checkin || !!myApp.checkout || !ev.checkout_available}
            variant="outline" className="h-14 rounded-2xl font-bold gap-2 bg-transparent border-accent/40 text-foreground disabled:opacity-40">
            {busy === "checkout" ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOut className="h-5 w-5" />}{myApp.checkout ? "Check-out feito" : "Fazer check-out"}
          </Button>
        </div>
      )}
      {isTeam && myApp && !ev.checkin_available && <p className="text-xs text-amber-400 -mt-2" data-testid="checkin-unavailable-msg">Check-in disponível a partir das {ev.checkin_release}.</p>}
      {isTeam && myApp?.checkin && !myApp?.checkout && !ev.checkout_available && <p className="text-xs text-amber-400 -mt-2" data-testid="checkout-unavailable-msg">Check-out disponível após o término do evento.</p>}

      {/* Equipe ao vivo */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head text-lg font-bold">Equipe no evento {team ? `(${team.count})` : ""}</h2>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="live-indicator">Atualizando em tempo real <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /></span>
        </div>
        {!team ? <div className="text-sm text-muted-foreground">Carregando equipe…</div>
          : team.members.length === 0 ? <div className="rounded-2xl border border-white/10 bg-card p-8 text-center text-sm text-muted-foreground" data-testid="team-empty">Nenhum músico confirmado ainda.</div>
          : (
            <div className="space-y-2.5" data-testid="team-list">
              {team.members.map((m) => {
                const st = memberStatus(m, team.checkout_available);
                return (
                  <div key={m.application_id} data-testid={`team-member-${m.user_id}`} className={`flex items-center gap-3 rounded-2xl border p-3 ${m.is_me ? "border-accent/40 bg-accent/[0.04]" : "border-white/10 bg-card"}`}>
                    <div className="h-11 w-11 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center font-bold shrink-0">
                      {m.photo ? <img src={m.photo} alt="" className="h-full w-full object-cover" /> : (m.stage_name?.[0] || "?")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{m.stage_name}{m.is_me && <span className="text-xs text-accent font-normal"> · você</span>}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.instrument || "—"}</div>
                    </div>
                    <span className={`flex items-center gap-1.5 text-sm font-medium shrink-0 ${st.cls}`}><st.Icon className="h-4 w-4" />{st.label}</span>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* Rodapé info */}
      <button data-testid="manual-checkin-info" onClick={() => navigate(`/eventos/${id}`)} className="w-full flex items-center gap-3 rounded-2xl border border-sky-500/25 bg-sky-500/[0.05] p-4 text-left">
        <Info className="h-5 w-5 text-sky-400 shrink-0" />
        <span className="flex-1 text-sm text-muted-foreground">Em caso de imprevistos, o criador do evento pode registrar o check-in manualmente.</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </button>
    </div>
  );
}
